import React, {useCallback} from "react";
import {
  FormCard,
  FormSlide,
  TextField,
  StatesField,
  CheckboxItem,
} from "@reside/ui";
import {RoleNameEnum} from "../../utils/types/common";
import {AdminRoles} from "../admin-roles";
import {ThemesGalery} from "../../pages/page-admin-organizations-branding/atoms/Themes/ThemesGalery";
import {CreateOrganizationRequest} from "../../utils/types/organization";
import {CreateOrUpdateOrganization} from "./organizationFormConstants";

type OrganizationDetailsCardProps = Readonly<{
  values: CreateOrUpdateOrganization;
  showDeactivateCheckbox: boolean;
  allActiveDomainsPresent: boolean;
  removedActiveDomains: string;
  onDeactivateUsersChange: () => void;
}>;

export const OrganizationDetailsCard = ({
  values,
  showDeactivateCheckbox,
  allActiveDomainsPresent,
  removedActiveDomains,
  onDeactivateUsersChange,
}: OrganizationDetailsCardProps) => (
  <FormCard title="Organization">
    <TextField label="Name" name="name" />
    <TextField label="Description" name="description" />
    <TextField label="Email Address" name="email" type="email" />
    <TextField label="Phone" name="phone" format="us-phone" />
    <TextField label="Domains" name="domains" capitalize={false} />
    {showDeactivateCheckbox && !allActiveDomainsPresent && (
      <CheckboxItem
        label={`Deactivate users with email domains: ${removedActiveDomains}`}
        name="deactivateUsersWithoutWhitelistEmail"
        onChange={onDeactivateUsersChange}
        checked={values.deactivateUsersWithoutWhitelistEmail}
      >
        <p>
          <strong style={{color: "red"}}>NB!</strong> When selected, active/new
          users with email domains that are not specified in the list will be
          deactivated.
        </p>
      </CheckboxItem>
    )}
  </FormCard>
);

export const AddressCard = () => (
  <FormCard title="Address">
    <TextField label="Address (234 Main St.)" name="address.street" />
    <TextField
      label="Address line 2 (Apartment, suite, unit)"
      name="address.number"
    />
    <TextField label="City" name="address.city" />
    <StatesField label="State" name="address.state" />
    <TextField label="Zip Code" name="address.zipcode" format="zip-code" />
  </FormCard>
);

type BrandingCardProps = Readonly<{
  organizationId: string;
}>;

export const BrandingCard = ({organizationId}: BrandingCardProps) => (
  <AdminRoles
    roles={[RoleNameEnum.RESIDE_ADMIN, RoleNameEnum.ORGANIZATION_ADMIN]}
  >
    <FormCard title="Resident Experience Branding">
      <ThemesGalery organizationId={organizationId} />
    </FormCard>
  </AdminRoles>
);

type FormContentProps = Readonly<{
  values: CreateOrUpdateOrganization;
  setFieldValue: (field: string, value: boolean) => void;
  initialValues?: CreateOrganizationRequest;
  showDeactivateCheckbox: boolean;
  allActiveDomainsPresent: (values: CreateOrUpdateOrganization) => boolean;
  removedActiveDomains: (values: CreateOrUpdateOrganization) => string;
}>;

export const FormContent = ({
  values,
  setFieldValue,
  initialValues,
  showDeactivateCheckbox,
  allActiveDomainsPresent,
  removedActiveDomains,
}: FormContentProps) => {
  const handleDeactivateUsersChange = useCallback(
    () =>
      setFieldValue(
        "deactivateUsersWithoutWhitelistEmail",
        !values.deactivateUsersWithoutWhitelistEmail,
      ),
    [setFieldValue, values.deactivateUsersWithoutWhitelistEmail],
  );

  return (
    <FormSlide>
      <OrganizationDetailsCard
        values={values}
        showDeactivateCheckbox={showDeactivateCheckbox}
        allActiveDomainsPresent={allActiveDomainsPresent(values)}
        removedActiveDomains={removedActiveDomains(values)}
        onDeactivateUsersChange={handleDeactivateUsersChange}
      />
      <AddressCard />
      {initialValues && <BrandingCard organizationId={initialValues.id} />}
    </FormSlide>
  );
};
